import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Brief 3.5 PR 2 — three-tab control for AdminMerchantDetail.
 *
 * Tab state lives in the URL hash. Active tab is derived from
 * `location.hash` on every render — no local state, no sync effect,
 * no race conditions.
 *
 * Tab clicks PUSH a new history entry so browser back/forward walks
 * through tab history (Brief 3.5 PR 2 follow-up). Only the very first
 * sync (empty hash → default tab) uses replace, so the operator can
 * still navigate back out of the merchant detail page.
 *
 * Active tab content is rendered lazily — only the active tab's
 * children mount. Saves render time on the heavy Account tab.
 */
export default function MerchantTabs({ tabs, merchantId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const hashKey = (location.hash || "").replace(/^#/, "");
  // Active = whichever visible tab matches the URL hash; fall back to
  // the first visible tab if the hash is empty / unknown.
  const matched = visibleTabs.find((t) => t.key === hashKey);
  const active = matched ? matched.key : visibleTabs[0].key;

  const onSelect = useCallback((key) => {
    const isInitialSync = !hashKey;
    navigate(
      `${location.pathname}${location.search}#${key}`,
      { replace: isInitialSync },
    );
  }, [hashKey, location.pathname, location.search, navigate]);

  const activeTab = visibleTabs.find((t) => t.key === active) || visibleTabs[0];

  return (
    <div data-testid="merchant-tabs" className="mt-4">
      <div
        role="tablist"
        aria-label="Merchant detail tabs"
        className="flex items-center gap-1 border-b border-white/[0.06]"
      >
        {visibleTabs.map(({ key, label, icon: Icon, badge }) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`merchant-tab-panel-${key}`}
              id={`merchant-tab-${key}`}
              data-testid={`merchant-tab-${key}`}
              onClick={() => onSelect(key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] -mb-px border-b-2 transition-colors ${
                isActive
                  ? "border-cyan-400/70 text-white"
                  : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />} {label}
              {badge !== undefined && badge !== null && badge !== 0 && (
                <span
                  data-testid={`merchant-tab-${key}-badge`}
                  className="ml-1 inline-flex items-center justify-center rounded-full bg-amber-400/[0.16] border border-amber-400/30 px-1.5 text-[10px] text-amber-100"
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        id={`merchant-tab-panel-${active}`}
        role="tabpanel"
        aria-labelledby={`merchant-tab-${active}`}
        data-testid={`merchant-tab-panel-${active}`}
        data-merchant-id={merchantId}
        className="pt-6 space-y-7"
      >
        {activeTab.render()}
      </div>
    </div>
  );
}
